import { useState } from 'react';
import { useNotify, useRecordContext, useRefresh } from 'react-admin';
import {
  Box, Typography, Button, IconButton, Stack, Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import {
  tbl, btn, typo, badgeStyle, WEBSITE_PUBLICATION_STATE, WEBSITE_FAMILY_LABELS,
} from '../styles/ds';
import { websiteRequest } from './websiteApi';

/**
 * Manual display order of the listings inside this category (register #45).
 * A listing linked to several categories (WD-028) has its own position in
 * each. Saved separately from the category's details.
 */
const CategoryListingsOrder = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [order, setOrder] = useState(null);
  const [saving, setSaving] = useState(false);
  if (!record?.listings) return null;
  const rows = order ?? record.listings;

  const move = (i, d) => {
    const next = [...rows];
    [next[i], next[i + d]] = [next[i + d], next[i]];
    setOrder(next);
  };
  const save = async () => {
    setSaving(true);
    try {
      await websiteRequest(`/website_categories/${record.id}/order`, 'PUT', {
        version: record.version,
        items: rows.map((l, i) => ({ product_id: l.id, sort_position: i })),
      });
      notify('סדר המוצרים בקטגוריה נשמר', { type: 'success' });
      setOrder(null);
      refresh();
    } catch (err) { notify(err.message, { type: 'error' }); }
    finally { setSaving(false); }
  };

  return (
    <Box mt={3} width="100%">
      <Typography sx={{ ...typo.sectionTitle, mb: 0.5 }}>סדר המוצרים בקטגוריה</Typography>
      <Typography sx={{ ...typo.meta, mb: 1.5 }}>
        הסדר שבו המוצרים מוצגים באתר בקטגוריה זו (ברירת המחדל; המבקרים יכולים למיין גם לפי מחיר או שם).
      </Typography>
      {!rows.length ? (
        <Typography fontSize={13} color="text.secondary">אין מוצרי אתר בקטגוריה זו.</Typography>
      ) : (
        <TableContainer component={Box} sx={tbl.container}>
          <Box className="crm-table-scroll" data-mobile-scroll tabIndex={0} role="region" aria-label="סדר מוצרים"><Table size="small">
            <TableHead><TableRow>
              <TableCell>#</TableCell>
              <TableCell>מוצר</TableCell>
              <TableCell align="center">מצב</TableCell>
              <TableCell>משפחה</TableCell>
              <TableCell align="center">הזזה</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {rows.map((l, i) => (
                <TableRow key={l.id} sx={tbl.zebraRow(i)}>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell>
                    <Typography fontSize={13} fontWeight={600} dir="ltr" textAlign="right">{l.name_en}</Typography>
                    <Typography fontSize={11} color="text.secondary">{l.is_primary ? 'קטגוריה ראשית' : 'קטגוריה נוספת'}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <span style={badgeStyle(WEBSITE_PUBLICATION_STATE, l.publication_state)}>{WEBSITE_PUBLICATION_STATE[l.publication_state]?.label}</span>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{WEBSITE_FAMILY_LABELS[l.family]}</TableCell>
                  <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                    <IconButton size="small" aria-label="הזזה למעלה" disabled={i === 0 || saving} onClick={() => move(i, -1)}><ArrowUpwardIcon fontSize="small" /></IconButton>
                    <IconButton size="small" aria-label="הזזה למטה" disabled={i === rows.length - 1 || saving} onClick={() => move(i, 1)}><ArrowDownwardIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table></Box>
        </TableContainer>
      )}
      {order && (
        <Stack direction="row" spacing={1.5} mt={1.5}>
          <Button variant="contained" disabled={saving} onClick={save} sx={btn.primary}>שמירת הסדר</Button>
          <Button variant="outlined" disabled={saving} onClick={() => setOrder(null)} sx={btn.cancel}>ביטול</Button>
        </Stack>
      )}
    </Box>
  );
};
export default CategoryListingsOrder;
